import * as Yup from 'yup';
import React, { useEffect, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import palette from 'src/theme/palette';
import InputField from 'src/components/InputField';
import { Form, FormikProvider, useFormik } from 'formik';
import CustomDropdown from 'src/components/CustomDropdown';
import { api } from 'src/Api';
import { currencyData } from 'src/asset/Currency';
import { toast } from 'react-toastify';
import LoadingButtonWrapper from 'src/components/LoadingButtonWrapper';
import moment from 'moment';
import { TableNoData } from 'src/components/NoData';
import ProjectManagerDropdown from 'src/components/ProjectManagerDropdown';
import { LoadingButton } from '@mui/lab';
import Iconify from 'src/components/Iconify';
import Searchbar from '../dashboard/Searchbar';

const InviteModal = ({ open, onClose, data, isEdit, refresh }) => {
  const token = localStorage.getItem('token');
  const [roleList, setRoleList] = useState([]);
  const [userList, setUserList] = useState([]);
  const [inviteList, setInviteList] = useState([]);
  const [saveLoading, setSaveLoading] = useState(false);
  const [inviteActionLoader, setInviteActionLoader] = useState(null);
  const [inviteCancelActionLoader, setInviteCancelActionLoader] = useState(null);
  const [saveLoadingExist, setSaveLoadingExist] = useState(false);
  const [loading, setLoading] = useState(false);

  const [value, setValue] = useState(0);
  const [search, setsearch] = useState('');

  // FOR EXISTING USER
  const [selectedUserIds, setSelectedUserIds] = useState([]); // multiple selected IDs
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [errorsExist, setErrorsExist] = useState({});

  // Add new user
  const handleAddUsers = (userIds) => {
    setSelectedUserIds(userIds);

    const newUsers = userIds
      .map((id) => {
        const existing = selectedUsers.find((u) => u.user_id === id);
        if (existing) return existing; // agar pehle se hai to wahi return karo (role preserve ho)

        const user = userList.find((u) => u.id === id);
        return user
          ? { user_id: user.id, project_id: data?.id, role_id: 5 } // ✅ default role 5
          : null;
      })
      .filter(Boolean);

    setSelectedUsers(newUsers);
  };

  // Change role
  const handleRoleChange = (userId, newRole) => {
    setSelectedUsers((prev) =>
      prev.map((u) => (u.user_id === userId ? { ...u, role_id: newRole } : u))
    );
    setErrorsExist((prev) => ({ ...prev, [userId]: '' }));
  };

  // Validate before submit
  const validate = () => {
    let valid = true;
    let newErrors = {};

    if (selectedUsers.length === 0) {
      newErrors.users = 'At least one user is required';
      valid = false;
    }

    selectedUsers.forEach((u) => {
      if (!u.role_id) {
        newErrors[u.user_id] = 'Role is required';
        valid = false;
      }
    });

    setErrorsExist(newErrors);
    return valid;
  };

  // Submit handler
  const handleSubmitExist = () => {
    if (!validate()) return;

    setSaveLoadingExist(true);
    const token = localStorage.getItem('token');
    const myHeaders = new Headers();
    myHeaders.append('Authorization', `Bearer ${token}`);

    const formData = new FormData();
    selectedUsers && formData.append('users', JSON.stringify(selectedUsers));

    const requestOptions = {
      method: 'POST',
      redirect: 'follow',
      headers: myHeaders,
      body: formData
    };

    fetch(api.invite_bulk_user, requestOptions)
      .then((response) =>
        response.text().then((result) => ({ status: response.status, body: result }))
      )
      .then(({ status, body }) => {
        const result = JSON.parse(body);
        if (status === 200 || status === 201) {
          toast.success(result.message);
          refresh();
          setSelectedUserIds([]);
          setSelectedUsers([]);
          setErrorsExist({});
          getInviteList();
        } else {
          toast.error(result.error);
        }
      })
      .catch((error) => {
        toast.error(error.error);
        console.log('error', error);
      })
      .finally(() => {
        setSaveLoadingExist(false);
      });
  };

  /////////////////////////////////////////////////////////////////////////////////

  const EditAddSchema = Yup.object().shape({
    first_name: Yup.string().required('First Name is required'),
    last_name: Yup.string().required('Last Name is required'),
    email: Yup.string().email('Email must be a valid email address').required('Email is required'),
    role: Yup.string().required('Role is required')
  });

  const formik = useFormik({
    enableReinitialize: true, // ✅ Add this line
    initialValues: {
      first_name: data?.first_name || '',
      last_name: data?.last_name || '',
      email: data?.email || '',
      role: data?.role?.id || ''
    },
    validationSchema: EditAddSchema,
    onSubmit: () => {
      handleSave();
    }
  });

  const { errors, touched, values, handleSubmit, getFieldProps } = formik;

  useEffect(() => {
    if (data?.id) {
      getInviteList();
      getRoleData();
      getExistingUserData();
    }
  }, [data]);

  function getRoleData() {
    const myHeaders = new Headers();
    myHeaders.append('Authorization', `Bearer ${token}`);

    const requestOptions = {
      method: 'GET',
      redirect: 'follow',
      headers: myHeaders
    };

    fetch(api.get_roles, requestOptions)
      .then((response) =>
        response.text().then((result) => ({ status: response.status, body: result }))
      )
      .then(({ status, body }) => {
        const result = JSON.parse(body);
        if (status === 200 || status === 201) {
          setRoleList(result?.data);
        } else {
          toast.error(result.error);
        }
      })
      .catch((error) => {
        console.log('error', error);
      })
      .finally(() => {});
  }

  function getExistingUserData() {
    const myHeaders = new Headers();
    myHeaders.append('Authorization', `Bearer ${token}`);

    const queryParams = new URLSearchParams();
    queryParams.append('project_id', data?.id);

    const requestOptions = {
      method: 'GET',
      redirect: 'follow',
      headers: myHeaders
    };

    fetch(`${api.get_existing_user_list}?${queryParams.toString()}`, requestOptions)
      .then((response) =>
        response.text().then((result) => ({ status: response.status, body: result }))
      )
      .then(({ status, body }) => {
        const result = JSON.parse(body);
        if (status === 200 || status === 201) {
          setUserList(result?.data);
        } else {
          toast.error(result.error);
        }
      })
      .catch((error) => {
        console.log('error', error);
      })
      .finally(() => {});
  }

  useEffect(() => {
    if (search.length == 0 && data?.id) {
      inviteList.length = 0;
      getInviteList(1);
    }
  }, [search]);

  function getInviteList() {
    setLoading(true);
    const myHeaders = new Headers();
    myHeaders.append('Authorization', `Bearer ${token}`);

    const queryParams = new URLSearchParams();
    queryParams.append('project_id', data?.id);
    search.length > 0 && queryParams.append('search', search);

    const requestOptions = {
      method: 'GET',
      redirect: 'follow',
      headers: myHeaders
    };

    fetch(`${api.invite_list}?${queryParams.toString()}`, requestOptions)
      .then((response) =>
        response.text().then((result) => ({ status: response.status, body: result }))
      )
      .then(({ status, body }) => {
        const result = JSON.parse(body);
        if (status === 200 || status === 201) {
          setInviteList(result?.data);
        } else {
          toast.error(result.error);
        }
      })
      .catch((error) => {
        console.log('error', error);
      })
      .finally(() => {
        setLoading(false);
      });
  }

  function handleSave() {
    setSaveLoading(true);
    const token = localStorage.getItem('token');
    const myHeaders = new Headers();
    myHeaders.append('Authorization', `Bearer ${token}`);

    const formData = new FormData();
    values?.first_name && formData.append('firstname', values?.first_name);
    values?.last_name && formData.append('lastname', values?.last_name);
    values?.email && formData.append('email', values?.email);
    data?.id && formData.append('project_id', data?.id);
    values?.role && formData.append('role_id', values?.role);

    const requestOptions = {
      method: 'POST',
      redirect: 'follow',
      headers: myHeaders,
      body: formData
    };

    fetch(api.invite_user, requestOptions)
      .then((response) =>
        response.text().then((result) => ({ status: response.status, body: result }))
      )
      .then(({ status, body }) => {
        const result = JSON.parse(body);
        if (status === 200 || status === 201) {
          toast.success(result.message);
          refresh();
          formik.resetForm();
          getInviteList();
        } else {
          toast.error(result.error);
        }
      })
      .catch((error) => {
        toast.error(error.error);
        console.log('error', error);
      })
      .finally(() => {
        setSaveLoading(false);
      });
  }

  function handleResendInviteFunction(row) {
    setInviteActionLoader(row?.id);
    const token = localStorage.getItem('token');
    const myHeaders = new Headers();
    myHeaders.append('Authorization', `Bearer ${token}`);

    const formData = new FormData();
    row?.id && formData.append('user_id', row?.id);
    formData.append('project_id', row?.project_id == 0 ? '0' : row?.project_id);
    row?.role?.id && formData.append('role_id', row?.role?.id);

    const requestOptions = {
      method: 'POST',
      redirect: 'follow',
      headers: myHeaders,
      body: formData
    };

    fetch(api.resand_invite, requestOptions)
      .then((response) =>
        response.text().then((result) => ({ status: response.status, body: result }))
      )
      .then(({ status, body }) => {
        const result = JSON.parse(body);
        if (status === 200 || status === 201) {
          toast.success(result.message);
          refresh();
          formik.resetForm();
          getInviteList();
        } else {
          toast.error(result.error);
        }
      })
      .catch((error) => {
        toast.error(error.error);
        console.log('error', error);
      })
      .finally(() => {
        setInviteActionLoader(null);
      });
  }

  function handleCancelInviteFunction(row) {
    setInviteCancelActionLoader(row?.id);
    const token = localStorage.getItem('token');
    const myHeaders = new Headers();
    myHeaders.append('Authorization', `Bearer ${token}`);

    const formData = new FormData();
    row?.id && formData.append('user_id', row?.id);
    formData.append('project_id', row?.project_id == 0 ? '0' : row?.project_id);
    row?.role?.id && formData.append('role_id', row?.role?.id);
    formData.append('status_id', 17);

    const requestOptions = {
      method: 'POST',
      redirect: 'follow',
      headers: myHeaders,
      body: formData
    };

    fetch(api.cancel_invite, requestOptions)
      .then((response) =>
        response.text().then((result) => ({ status: response.status, body: result }))
      )
      .then(({ status, body }) => {
        const result = JSON.parse(body);
        if (status === 200 || status === 201) {
          toast.success(result.message);
          refresh();
          formik.resetForm();
          getInviteList();
        } else {
          toast.error(result.error);
        }
      })
      .catch((error) => {
        toast.error(error.error);
        console.log('error', error);
      })
      .finally(() => {
        setInviteCancelActionLoader(null);
      });
  }

  const handleChangeTab = (event, newValue) => {
    setValue(newValue);
  };

  return (
    <Dialog
      open={open}
      onClose={() => {
        formik.resetForm(); // Reset form on modal close
        onClose();
        setValue(0);
        setInviteList([]);
        setSelectedUserIds([]);
        setSelectedUsers([]);
        setErrorsExist({});
      }}
      PaperProps={{
        sx: {
          width: '700px',
          borderRadius: 1,
          height: '75%'
        }
      }}
    >
      <DialogTitle sx={{ pb: 0, paddingLeft: 2, paddingRight: 2, paddingTop: 1 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',

            pb: 0
          }}
        >
          <Typography
            sx={{ fontSize: 16, color: palette.primary.titledark, fontFamily: 'poppinsBold' }}
          >
            Invite
          </Typography>
          <IconButton
            onClick={() => {
              formik.resetForm(); // Reset form on close button click
              onClose();
              setSelectedUserIds([]);
              setSelectedUsers([]);
              setErrorsExist({});
              setValue(0);
              setInviteList([]);
            }}
          >
            <CloseIcon style={{ color: palette.primary.disabled, fontSize: 20 }} />
          </IconButton>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Tabs
            value={value}
            onChange={handleChangeTab}
            textColor="primary"
            TabIndicatorProps={{ style: { display: 'none' } }} // hide underline
            aria-label="account tabs"
            sx={{
              minHeight: 35 // Tabs container ki height bhi kam
            }}
          >
            <Tab
              label="Invitations"
              sx={{
                minHeight: 35, // Tab ki height kam
                padding: '4px 10px', // upar niche kam spacing
                fontFamily: value === 0 ? 'poppinsMedium' : 'poppinsLight',
                color: value === 0 ? palette.primary.white : palette.primary.disabled,
                fontSize: value === 0 ? 13 : 12,
                backgroundColor:
                  value === 0 ? palette.primary.lightMain : palette.primary.inputborder,
                borderRadius: '6px',
                mr: 1
              }}
            />
            <Tab
              label="New User"
              sx={{
                minHeight: 35, // Tab ki height kam
                padding: '4px 10px', // upar niche kam spacing
                fontFamily: value === 1 ? 'poppinsMedium' : 'poppinsLight',
                color: value === 1 ? palette.primary.white : palette.primary.disabled,
                fontSize: value === 1 ? 13 : 12,
                backgroundColor:
                  value === 1 ? palette.primary.lightMain : palette.primary.inputborder,
                borderRadius: '6px',
                mr: 1
              }}
            />
            <Tab
              label="Existing User"
              sx={{
                minHeight: 35, // Tab ki height kam
                padding: '4px 10px', // upar niche kam spacing
                fontFamily: value === 2 ? 'poppinsMedium' : 'poppinsLight',
                color: value === 2 ? palette.primary.white : palette.primary.disabled,
                fontSize: value === 2 ? 13 : 12,
                backgroundColor:
                  value === 2 ? palette.primary.lightMain : palette.primary.inputborder,
                borderRadius: '6px',
                mr: 1
              }}
            />
          </Tabs>
        </Box>
      </DialogTitle>

      {/* INVITATIONS */}
      {value === 0 && (
        <DialogContent>
          {!loading && (
            <>
              <Grid container spacing={2} alignItems="center" mb={2}>
                {/* Search + Search Button */}
                <Grid item xs={12} md={11}>
                  <Grid container spacing={2} alignItems="center">
                    {/* Searchbar */}
                    <Grid item xs={12} sm={9} md={11}>
                      <Searchbar
                        width="100%"
                        value={search}
                        setFunction={(val) => setsearch(val)}
                        className="width_full_property"
                        placeholder="Search..."
                        onEnter={() => {
                          inviteList.length = 0;
                          getInviteList(1);
                        }}
                      />
                    </Grid>

                    {/* Search Button */}
                    <Grid item xs={12} sm={3} md={1}>
                      <LoadingButton
                        fullWidth
                        sx={{ height: 33, borderRadius: 0.7 }}
                        variant="contained"
                        onClick={() => {
                          getInviteList(1);
                        }}
                      >
                        <Iconify icon="material-symbols:search" sx={{ width: 20, height: 20 }} />
                      </LoadingButton>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>

              {inviteList.map((val) => {
                return (
                  <Stack
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 1,
                      borderBottom: 1,
                      borderColor: palette.primary.inputborder,
                      pb: 1
                    }}
                  >
                    <Box sx={{ display: 'flex', flex: 1, flexDirection: 'column' }}>
                      <Typography
                        sx={{
                          fontSize: 13,
                          color: palette.primary.titledark,
                          fontFamily: 'poppinsSemiBold'
                        }}
                      >
                        {val?.firstname + ' ' + val?.lastname}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: 11,
                          color: palette.primary.title,
                          fontFamily: 'poppinsRegular'
                        }}
                      >
                        {val?.email}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                      <Typography
                        sx={{
                          fontSize: 12,
                          color: palette.primary.main,
                          fontFamily: 'poppinsSemiBold',
                          backgroundColor: palette.primary.lighterMain,
                          padding: '1px 15px',
                          borderRadius: 10,
                          letterSpacing: 0.5,
                          mb: 0.5
                        }}
                      >
                        {val?.role?.role_name}
                      </Typography>
                      <Typography
                        sx={{ fontFamily: 'poppinsLight', ml: 1 }}
                        className={
                          (val?.status?.toLowerCase() == 'pending' && 'status-pending-small') ||
                          (val?.status?.toLowerCase() == 'expire' && 'status-red-small')
                        }
                      >
                        {val?.status.toLowerCase() == 'expire' ? 'EXPIRED' : val?.status}
                      </Typography>
                    </Box>
                    <Box sx={{ ml: 3 }}>
                      <Tooltip title="Resend Invite">
                        <LoadingButton
                          loading={val?.id == inviteActionLoader ? true : false}
                          size="small"
                          variant="contained"
                          onClick={() => handleResendInviteFunction(val)}
                          style={{
                            justifyContent: 'center',
                            width: 30,
                            height: 30,
                            minWidth: 30,
                            padding: 0,
                            alignSelf: 'center',
                            marginRight: 5,
                            borderRadius: 5
                          }}
                        >
                          <Iconify
                            style={{
                              cursor: 'pointer',
                              fontSize: 20,
                              color: '#ffffff'
                            }}
                            icon="streamline-plump:mail-send-solid"
                          />
                        </LoadingButton>
                      </Tooltip>

                      <Tooltip title="Cancel">
                        <LoadingButton
                          loading={val?.id == inviteCancelActionLoader ? true : false}
                          disabled={
                            val?.status.toLowerCase() == 'expire' ||
                            val?.id == inviteCancelActionLoader
                              ? true
                              : false
                          }
                          size="small"
                          variant="contained"
                          onClick={() => handleCancelInviteFunction(val)}
                          style={{
                            justifyContent: 'center',
                            width: 30,
                            height: 30,
                            minWidth: 30,
                            padding: 0,
                            alignSelf: 'center',
                            marginRight: 5,
                            borderRadius: 5,
                            backgroundColor:
                              val?.status.toLowerCase() == 'expire' ||
                              val?.id == inviteCancelActionLoader
                                ? palette.primary.inputborder
                                : 'red'
                          }}
                        >
                          <Iconify
                            style={{
                              cursor: 'pointer',
                              fontSize: 20,
                              color:
                                val?.status.toLowerCase() == 'expire' ||
                                val?.id == inviteCancelActionLoader
                                  ? palette.primary.disabled
                                  : '#ffffff'
                            }}
                            icon="basil:cancel-solid"
                          />
                        </LoadingButton>
                      </Tooltip>
                    </Box>
                  </Stack>
                );
              })}
            </>
          )}

          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 10.4 }}>
              <CircularProgress sx={{ color: palette.primary.main }} size={25} />
            </Box>
          )}

          {/* empty list */}
          {inviteList.length == 0 && !loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <TableNoData title="No Invitations" sx={{ padding: 5, borderBottom: 0 }} />
            </Box>
          )}
        </DialogContent>
      )}

      {/* NEW USERS */}
      {value === 1 && (
        <FormikProvider value={formik}>
          <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
            <DialogContent
              sx={{
                flex: 1,
                paddingLeft: 3,
                paddingRight: 3,
                paddingTop: 0
              }}
            >
              <Grid
                container
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  borderBottom: 1,
                  borderBottomColor: palette.primary.inputborder
                }}
              >
                {/* FIRST NAME */}
                <Grid item xs={5.8}>
                  <InputField
                    name="first_name"
                    label="First Name"
                    type="text"
                    placeholder=""
                    {...getFieldProps('first_name')}
                    error={touched.first_name && errors.first_name}
                    helperText={touched.first_name && errors.first_name}
                    fontFamily="poppinsRegular"
                  />
                </Grid>

                {/* LAST NAME */}
                <Grid item xs={5.8}>
                  <InputField
                    name="last_name"
                    label="Last Name"
                    type="text"
                    placeholder=""
                    {...getFieldProps('last_name')}
                    error={touched.last_name && errors.last_name}
                    helperText={touched.last_name && errors.last_name}
                    fontFamily="poppinsRegular"
                    labelFontMedium
                  />
                </Grid>

                {/* EMAIL */}
                <Grid item xs={5.8}>
                  <InputField
                    name="email"
                    label="Email"
                    type="text"
                    placeholder=""
                    {...getFieldProps('email')}
                    error={touched.email && errors.email}
                    helperText={touched.email && errors.email}
                    fontFamily="poppinsRegular"
                  />
                </Grid>

                {/* ROLE */}
                <Grid item xs={5.8}>
                  <CustomDropdown
                    label="Role"
                    name="role"
                    value={values.role}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    options={roleList.map((val) => ({
                      value: val?.id,
                      label: val?.role_name
                    }))}
                    placeholder=""
                    error={touched.role && Boolean(errors.role)}
                    helperText={touched.role && errors.role}
                    fontFamily="poppinsRegular"
                    labelFontMedium
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={{ mb: 1, mr: 1.5, pt: 0 }}>
              <Button
                onClick={() => {
                  formik.resetForm(); // Reset form on close button click
                  onClose();
                  setSelectedUserIds([]);
                  setSelectedUsers([]);
                  setErrorsExist({});
                  setValue(0);
                  setInviteList([]);
                }}
                variant="outlined"
                sx={{
                  ml: 1.5,
                  color: palette.primary.pageButtonTitle,
                  borderColor: palette.primary.pageButtonBorder,
                  fontSize: 12,
                  height: 30,
                  fontFamily: 'poppinsRegular',
                  borderRadius: 0.7,
                  '&:hover': {
                    backgroundColor: palette.primary.inputborder,
                    borderColor: palette.primary.pageButtonBorder
                  }
                }}
              >
                Cancel
              </Button>
              <LoadingButtonWrapper
                loading={saveLoading}
                type="submit"
                variant="contained"
                sx={{
                  width: saveLoading ? 120 : 90,
                  height: 30,
                  fontSize: 12,
                  borderRadius: 0.7,
                  fontFamily: 'poppinsLight',
                  letterSpacing: 0.3,
                  transition: 'width 0.3s ease, background-color 0.3s ease, transform 0.3s ease'
                }}
                title="Save"
              />
            </DialogActions>
          </Form>
        </FormikProvider>
      )}

      {/* EXISTING USERS */}
      {value === 2 && (
        <>
          <DialogContent
            sx={{
              overflowY: 'hidden' // ✅ Allows vertical scrolling
            }}
          >
            <Grid
              container
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: 1,
                borderBottomColor: palette.primary.inputborder
              }}
            >
              {/* ROLE */}
              <Grid item xs={12}>
                <ProjectManagerDropdown
                  label="Users"
                  name="users"
                  multiple // ✅ allow multi-select
                  value={selectedUserIds} // ✅ array of IDs
                  onChange={(e) => handleAddUsers(e.target.value)}
                  options={userList.map((val) => ({
                    value: val.id,
                    name: `${val.firstname} ${val.lastname}`
                  }))}
                  placeholder="Select Users"
                  backgroundColor="#fff"
                  fontFamily="poppinsRegular"
                  marginBottom="1rem"
                  labelFontMedium
                />
              </Grid>
            </Grid>

            {selectedUsers.length !== 0 && (
              <Typography
                sx={{
                  fontSize: 16,
                  color: palette.primary.titledark,
                  fontFamily: 'poppinsBold',
                  mb: 1,
                  mt: 2
                }}
              >
                Selected User
              </Typography>
            )}
            {selectedUsers.length !== 0 && (
              <Stack
                sx={{
                  overflow: selectedUsers.length === 0 ? 'hidden' : 'auto',
                  overflowY: selectedUsers.length === 0 ? 'hidden' : 'auto',
                  height: 220
                }}
              >
                {selectedUsers.map((u) => {
                  const userData = userList.find((usr) => usr.id === u.user_id);
                  return (
                    <Stack
                      key={u.user_id}
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 2
                      }}
                    >
                      {/* Left side: name + email */}
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar
                          alt={userData?.firstname + ' ' + userData?.lastname}
                          src={userData?.avatar}
                          sx={{ height: 40, width: 40, mr: 1 }}
                        />
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                          <Typography sx={{ fontSize: 13, fontFamily: 'poppinsSemiBold' }}>
                            {userData?.firstname + ' ' + userData?.lastname}
                          </Typography>
                          <Typography sx={{ fontSize: 11, fontFamily: 'poppinsRegular' }}>
                            {userData?.email}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Right side: role dropdown */}
                      <Box sx={{ minWidth: 150 }}>
                        <CustomDropdown
                          height={34}
                          marginBottom={'0rem'}
                          name="role"
                          value={u.role_id} // ✅ already 5 hoga by default
                          onChange={(e) => handleRoleChange(u.user_id, e.target.value)}
                          options={roleList.map((val) => ({
                            value: val.id,
                            label: val.role_name
                          }))}
                          placeholder="Select role"
                          error={!!errorsExist[u.user_id]}
                          helperText={errorsExist[u.user_id]}
                        />
                      </Box>
                    </Stack>
                  );
                })}
              </Stack>
            )}
          </DialogContent>
          <DialogActions sx={{ mb: 1, mr: 1.5 }}>
            <Button
              onClick={() => {
                onClose();
                setSelectedUserIds([]);
                setSelectedUsers([]);
                setErrorsExist({});
                setValue(0);
                setInviteList([]);
              }}
              variant="outlined"
              sx={{
                ml: 1.5,
                color: palette.primary.pageButtonTitle,
                borderColor: palette.primary.pageButtonBorder,
                fontSize: 12,
                height: 30,
                fontFamily: 'poppinsRegular',
                borderRadius: 0.7,
                '&:hover': {
                  backgroundColor: palette.primary.inputborder,
                  borderColor: palette.primary.pageButtonBorder
                }
              }}
            >
              Cancel
            </Button>
            <LoadingButtonWrapper
              loading={saveLoadingExist}
              variant="contained"
              sx={{
                width: saveLoadingExist ? 120 : 90,
                height: 30,
                fontSize: 12,
                borderRadius: 0.7,
                fontFamily: 'poppinsLight',
                letterSpacing: 0.3,
                transition: 'width 0.3s ease, background-color 0.3s ease, transform 0.3s ease'
              }}
              title="Save"
              onClick={handleSubmitExist}
            />
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};
export default InviteModal;
